import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  Fade,
} from "@mui/material";
import { keyframes } from "@mui/system";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { QRCode } from "react-qrcode-logo";
import PreviewPanel, { PANEL_HEIGHT } from "./components/PreviewPanel";
import { DEFAULT_CHAT_STARTER } from "../immersive-panel-launch/chatSeed";

const AI_API_URL = process.env.REACT_APP_AI_API_URL || "http://127.0.0.1:8000";
// const AI_API_URL = 'http://127.0.0.1:8000'; // process.env.REACT_APP_AI_API_URL;
const API_ORIGIN = "https://staging-new.boltplus.tv"; 

if (!AI_API_URL) {
  throw new Error("REACT_APP_AI_API_URL is not defined. Immersive panel setup requires the AI API.");
}

type ExperienceType = "event" | "stream";

type Stage =
  | "experience"
  | "event_details"
  | "event_features"
  | "stream_source"
  | "stream_about"
  | "stream_features"
  | "result";

export type FeatureDefinition = {
  id: string;
  label: string;
  tier: "basic" | "advanced";
  description: string;
};

type FeatureToggle = FeatureDefinition & { active: boolean };

export type CatalogResponse = {
  event: FeatureDefinition[];
  stream: FeatureDefinition[];
};

type RecommendationPayload = {
  experience_type: ExperienceType;
  selected_feature_ids: string[];
  event?: {
    title: string;
    description: string;
  };
  stream?: {
    media_source?: string | null;
    has_source: boolean;
    description: string;
  };
};

type RecommendationResponse = {
  session_key: string;
  magic_link: string;
  base_url: string;
  feature_toggles: FeatureToggle[];
  recommended_feature_ids: string[];
  embed_code_tsx: string;
  experience_summary: string;
};

type RecommendationResult = {
  sessionKey: string;
  baseUrl: string;
  initialMagicLink: string;
  featureToggles: FeatureToggle[];
  embedCodeTsx: string;
  experienceSummary: string;
};


// smoother motion
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const spinReverse = keyframes`
  from { transform: rotate(360deg); }
  to   { transform: rotate(0deg); }
`;

// very subtle scale so dots don’t look like they’re moving inward
const microPulse = keyframes`
  0%, 100% { transform: scale(0.98); opacity: 0.9; }
  50%      { transform: scale(1);    opacity: 1;   }
`;

export const FancyLoader: React.FC<{ title?: string; subtitle?: string }> = ({
  title = "Creating your panel…",
  subtitle = "We're assembling your configuration and generating the QR code",
}) => {
  return (
    <Box
      role="progressbar"
      aria-label="Loading"
      sx={{ display: "grid", placeItems: "center", minHeight: 400, gap: 4 }}
    >
      <Box sx={{ position: "relative", width: 120, height: 120 }}>
        {/* Ambient glow */}
        <Box
          sx={{
            position: "absolute",
            inset: -30,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)",
            animation: `${microPulse} 2000ms ease-in-out infinite`,
          }}
        />

        {/* Spinner ring */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: "3px solid transparent",
            borderTopColor: "#8B5CF6",
            borderRightColor: "#6366F1",
            animation: `${spin} 1200ms cubic-bezier(0.4, 0.0, 0.2, 1) infinite`,
            filter: "drop-shadow(0 0 8px rgba(139, 92, 246, 0.5))",
          }}
        />

        {/* Inner glow */}
        <Box
          sx={{
            position: "absolute",
            inset: 16,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          }}
        />
      </Box>

      <Box sx={{ textAlign: "center", px: 2, maxWidth: 420 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: "#FFFFFF", 
            fontWeight: 700,
            mb: 1,
            letterSpacing: "-0.01em"
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ 
            color: alpha("#FFFFFF", 0.6), 
            lineHeight: 1.6,
            fontSize: 14
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

const EVENT_FEATURE_FALLBACK: FeatureDefinition[] = [
  {
    id: "logistics_ai",
    label: "Logistics AI Q&A",
    tier: "advanced",
    description: "AI concierge that handles schedule, venue, and FAQ logistics questions in real time.",
  },
  {
    id: "chat_room",
    label: "Chat room for attendees",
    tier: "basic",
    description: "Bring everyone together in a moderated backchannel throughout the event.",
  },
  {
    id: "live_quizzes",
    label: "Live Quizzes for attendees",
    tier: "basic",
    description: "Rapid-fire quizzes and polls to keep the room energized.",
  },
  {
    id: "attendee_blasts",
    label: "Send blasts to attendees",
    tier: "basic",
    description: "Broadcast quick reminders or programming updates instantly.",
  },
  {
    id: "voice_ai",
    label: "Voice AI Q&A",
    tier: "advanced",
    description: "Hands-free voice assistant for staff or attendees on the move.",
  },
  {
    id: "affiliate_store",
    label: "Affiliate merchandise store",
    tier: "basic",
    description: "Curate merch and partner offers alongside the live experience.",
  },
  {
    id: "ai_moderation",
    label: "AI moderation for attendees chat",
    tier: "advanced",
    description: "Keep the conversation on brand with proactive AI moderation.",
  },
];

const STREAM_FEATURE_FALLBACK: FeatureDefinition[] = [
  {
    id: "ai_chat_engagement",
    label: "AI engagement in chat",
    tier: "advanced",
    description: "An AI co-host keeps chat lively with prompts tuned to the stream.",
  },
  {
    id: "ai_qa",
    label: "AI Q&A",
    tier: "advanced",
    description: "Give viewers contextual answers about what they're watching in seconds.",
  },
  {
    id: "chat_room",
    label: "Chat room for viewers",
    tier: "basic",
    description: "Classic live chat anchored next to the player for community vibes.",
  },
  {
    id: "live_quizzes",
    label: "Live Quizzes for viewers",
    tier: "basic",
    description: "Keep viewers engaged with quick quizzes and polls.",
  },
  {
    id: "highlights",
    label: "Stream/movie highlights",
    tier: "advanced",
    description: "Auto-generate highlight reels from key stream moments.",
  },
  {
    id: "voice_ai",
    label: "Voice AI Q&A",
    tier: "advanced",
    description: "Voice-powered assistant for hands-free control and answers.",
  },
  {
    id: "affiliate_store",
    label: "Affiliate merchandise store",
    tier: "basic",
    description: "Merch modules tailored to what's on screen.",
  },
  {
    id: "ai_moderation",
    label: "AI moderation in chat",
    tier: "advanced",
    description: "AI filters to keep live chat welcoming and brand-safe.",
  },
  {
    id: "smart_ads",
    label: "Smart advertisement display",
    tier: "advanced",
    description: "Adaptive ad placements triggered by viewer persona and stream cues.",
  },
  {
    id: "stream_understanding",
    label: "Stream understanding (24/7 AI listener)",
    tier: "advanced",
    description: "Always-on listener builds deep context for downstream automations.",
  },
];

export const FALLBACK_CATALOG: CatalogResponse = {
  event: EVENT_FEATURE_FALLBACK,
  stream: STREAM_FEATURE_FALLBACK,
};

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      width: "100%",
      background: "linear-gradient(135deg, #0A0F1C 0%, #141B2D 50%, #0F1420 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: { xs: 4, md: 8 },
      px: { xs: 2, md: 4 },
    }}
  >
    {children}
  </Box>
);

 const SectionHeader: React.FC<{
   stage: Stage;
   experienceType: ExperienceType | null;
   logo?: React.ReactNode;
 }> = ({ stage, experienceType, logo }) => {
  const stageCopy: Record<Stage, { title: string; subtitle?: string }> = {
    experience: {
      title: "Choose your experience"
    },
    event_details: {
      title: "What is the event about?",
      subtitle: "Copy paste your event page."
    },
    event_features: {
      title: "What would you like the immersive panel to help with?",
      subtitle: "(select multiple)"
    },
    stream_source: {
      title: "Add your stream source",
    },
    stream_about: {
      title: "What is the stream about?",
    },
    stream_features: {
      title: "What would you like the immersive panel to help with?",
      subtitle: "(select multiple)"
    },
    result: {
      title: "Your panel is ready",
    },
  };

  const content = stageCopy[stage];

  return (
    <Box sx={{ mb: { xs: 6, md: 10 }, textAlign: "center", maxWidth: 1125, mx: "auto" }}>
      {logo && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 3, md: 4 } }}>
          {logo}
        </Box>
      )}
      <Typography
        variant="h1"
        sx={{
          background: "linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.75) 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: 800,
          fontSize: { xs: 40, sm: 56, md: 72 },
          lineHeight: 1.1,
          letterSpacing: "-0.04em",
          mb: content.subtitle ? 2 : 0,
        }}
      >
        {content.title}
      </Typography>
      {content.subtitle && (
        <Typography
          variant="h6"
          sx={{
            color: alpha("#FFFFFF", 0.5),
            fontWeight: 400,
            fontSize: { xs: 18, md: 22 },
            lineHeight: 1.6,
            maxWidth: 875,
            mx: "auto",
          }}
        >
          {content.subtitle}
        </Typography>
      )}
    </Box>
  );
};

const OptionCard: React.FC<{
  title: string;
  description: string;
  onClick: () => void;
  selected: boolean;
}> = ({ title, description, onClick, selected }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        minHeight: 260,
        background: selected
          ? "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)"
          : "transparent",
        borderRadius: 6,
        border: `2px solid ${selected ? alpha("#6366F1", 0.5) : alpha("#FFFFFF", 0.08)}`,
        p: { xs: 4, md: 5 },
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-8px)",
          borderColor: alpha("#6366F1", 0.7),
          background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%)",
        },
      }}
    >
      {selected && (
        <CheckCircleIcon
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            color: "#6366F1",
            fontSize: 28,
          }}
        />
      )}
      <Typography
        variant="h4"
        sx={{
          color: "#FFFFFF",
          fontWeight: 700,
          fontSize: { xs: 24, md: 30 },
          lineHeight: 1.3,
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: alpha("#FFFFFF", 0.6),
          lineHeight: 1.7,
          fontSize: { xs: 16, md: 18 },
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const FeatureSelector: React.FC<{
  features: FeatureDefinition[];
  selectedIds: string[];
  onToggle: (featureId: string) => void;
}> = ({ features, selectedIds, onToggle }) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoverTimers, setHoverTimers] = useState<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleMouseEnter = (featureId: string) => {
    const timer = setTimeout(() => {
      setHoveredId(featureId);
    }, 1000);
    setHoverTimers((prev) => ({ ...prev, [featureId]: timer }));
  };

  const handleMouseLeave = (featureId: string) => {
    const timer = hoverTimers[featureId];
    if (timer) {
      clearTimeout(timer);
      setHoverTimers((prev) => {
        const newTimers = { ...prev };
        delete newTimers[featureId];
        return newTimers;
      });
    }
    setHoveredId(null);
  };

  useEffect(() => {
    return () => {
      Object.values(hoverTimers).forEach((timer) => clearTimeout(timer));
    };
  }, [hoverTimers]);

  return (
    <Box sx={{ maxWidth: 1125, mx: "auto", width: "100%" }}>
      <Grid
        container
        spacing={3}
        justifyContent="center"     // keeps it centered if last row has a single item
      >
        {features.map((feature) => {
          const isActive = selectedIds.includes(feature.id);
          const isHovered = hoveredId === feature.id;

          return (
            <Grid item xs={12} md={6} key={feature.id}>
              <Box
                onClick={() => onToggle(feature.id)}
                onMouseEnter={() => handleMouseEnter(feature.id)}
                onMouseLeave={() => handleMouseLeave(feature.id)}
                sx={{
                  position: "relative",
                  height: "100%", // equal height cards in the grid
                  background: isActive
                    ? "linear-gradient(90deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)"
                    : "transparent",
                  borderRadius: 4,
                  border: `2px solid ${isActive ? alpha("#6366F1", 0.5) : alpha("#FFFFFF", 0.06)}`,
                  px: { xs: 3, md: 4 },
                  py: { xs: 3, md: 3.5 },
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    borderColor: alpha("#6366F1", 0.6),
                    background: isActive
                      ? "linear-gradient(90deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)"
                      : alpha("#FFFFFF", 0.03),
                  },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    {isActive && (
                      <CheckCircleIcon
                        sx={{
                          color: "#6366F1",
                          fontSize: 24,
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#FFFFFF",
                            fontWeight: 600,
                            fontSize: { xs: 18, md: 20 },
                          }}
                        >
                          {feature.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={feature.tier === "advanced" ? "Premium" : "Core"}
                          sx={{
                            bgcolor:
                              feature.tier === "advanced"
                                ? alpha("#8B5CF6", 0.15)
                                : alpha("#10B981", 0.15),
                            color: feature.tier === "advanced" ? "#A78BFA" : "#34D399",
                            fontWeight: 600,
                            fontSize: 12,
                            height: 24,
                          }}
                        />
                      </Stack>
                      <Fade in={isHovered} timeout={400}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: alpha("#FFFFFF", 0.6),
                            mt: 1,
                            fontSize: { xs: 14, md: 15 },
                            lineHeight: 1.6,
                            display: isHovered ? "block" : "none",
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Fade>
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const FeatureTuner: React.FC<{
  featureToggles: FeatureToggle[];
  onChange: (featureId: string, active: boolean) => void;
}> = ({ featureToggles, onChange }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const basic = featureToggles.filter((f) => f.tier === "basic");
  const advanced = featureToggles.filter((f) => f.tier === "advanced");

  const renderSection = (title: string, subtitle: string, items: FeatureToggle[], gradient: string) => (
    <Box sx={{ mb: 3 }}>
      {/* Section Header */}
      <Box
        sx={{
          mb: 2,
          pb: 1.5,
          borderBottom: `2px solid ${alpha("#FFFFFF", 0.08)}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: 16,
            mb: 0.5,
            letterSpacing: 0.3,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: alpha("#FFFFFF", 0.5),
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      {/* Feature Items */}
      <Stack spacing={1}>
        {items.map((feature) => {
          const isExpanded = expandedId === feature.id;
          
          return (
            <Box
              key={feature.id}
              sx={{
                position: "relative",
                borderRadius: 2.5,
                overflow: "hidden",
                border: `1px solid ${
                  feature.active
                    ? alpha("#6366F1", 0.4)
                    : alpha("#FFFFFF", 0.06)
                }`,
                bgcolor: feature.active
                  ? alpha("#6366F1", 0.04)
                  : alpha("#FFFFFF", 0.01),
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  bgcolor: feature.active
                    ? alpha("#6366F1", 0.08)
                    : alpha("#FFFFFF", 0.03),
                  borderColor: feature.active
                    ? alpha("#6366F1", 0.5)
                    : alpha("#FFFFFF", 0.12),
                  transform: "translateX(2px)",
                },
              }}
            >
              {/* Active indicator */}
              {feature.active && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: gradient,
                  }}
                />
              )}

              {/* Main Content */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  pl: feature.active ? 2.5 : 2,
                }}
              >
                <Box sx={{ flex: 1, mr: 2, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: 1.4,
                      }}
                    >
                      {feature.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={feature.tier === "advanced" ? "Premium" : "Core"}
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        bgcolor:
                          feature.tier === "advanced"
                            ? alpha("#8B5CF6", 0.12)
                            : alpha("#10B981", 0.12),
                        color:
                          feature.tier === "advanced" ? "#C4B5FD" : "#6EE7B7",
                        border: `1px solid ${
                          feature.tier === "advanced"
                            ? alpha("#8B5CF6", 0.3)
                            : alpha("#10B981", 0.3)
                        }`,
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                    <Tooltip
                      title={isExpanded ? "Click to collapse" : "Click to expand details"}
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        onClick={() =>
                          setExpandedId(isExpanded ? null : feature.id)
                        }
                        sx={{
                          color: alpha("#FFFFFF", 0.35),
                          fontSize: 16,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: alpha("#FFFFFF", 0.7),
                            transform: "scale(1.1)",
                          },
                        }}
                      />
                    </Tooltip>
                  </Stack>

                  {/* Expandable Description */}
                  <Collapse in={isExpanded} timeout={300}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha("#FFFFFF", 0.55),
                        fontSize: 12.5,
                        lineHeight: 1.6,
                        display: "block",
                        mt: 1,
                        pr: 1,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Collapse>
                </Box>

                {/* Switch */}
                <Switch
                  checked={feature.active}
                  onChange={(e) => onChange(feature.id, e.target.checked)}
                  sx={{
                    flexShrink: 0,
                    "& .MuiSwitch-switchBase": {
                      "&.Mui-checked": {
                        color: "#6366F1",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "#6366F1",
                          opacity: 0.5,
                        },
                      },
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: alpha("#FFFFFF", 0.15),
                    },
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );

  return (
    <Box>
      {renderSection(
        "Core Experiences",
        "Essential features to engage your community",
        basic,
        "linear-gradient(180deg, #10B981 0%, #059669 100%)"
      )}
      {renderSection(
        "Premium Intelligence",
        "Advanced AI capabilities for deeper engagement",
        advanced,
        "linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)"
      )}
    </Box>
  );
};


const buildMagicLink = (
  baseUrl: string,
  sessionKey: string,
  features: string[]
): string => {
  const sortedFeatures = [...features].sort();
  try {
    const url = new URL(baseUrl);
    url.searchParams.set("session", sessionKey);
    url.searchParams.set("features", sortedFeatures.join(","));
    return url.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}session=${sessionKey}&features=${sortedFeatures.join(
      ","
    )}`;
  }
};

const buildEmbedCode = (sessionKey: string, features: string[]): string => {
  const formatted = JSON.stringify([...features].sort(), null, 2);
  return (
    'import { BoltPlusImmersivePanel } from "@boltplus/immersive-panel";\n\n' +
    "export const ImmersivePanelEmbed = () => (\n" +
    "  <BoltPlusImmersivePanel\n" +
    `    sessionKey="${sessionKey}"\n` +
    `    features={ ${formatted} }\n` +
    '    style={{ height: "100%", width: "100%" }}\n' +
    "  />\n" +
    ");"
  );
};

const RecommendationView: React.FC<{
  features: FeatureDefinition[];
  result: RecommendationResult;
  onRestart: () => void;
  onFeaturesChange?: (features: string[]) => void;
}> = ({ features, result, onRestart, onFeaturesChange }) => {
  const PREVIEW_HEADER_H = 56; // the height of the badge/label row above the preview
  const PREVIEW_TOTAL_H = PANEL_HEIGHT + PREVIEW_HEADER_H;
  const [featureStates, setFeatureStates] = useState<Record<string, boolean>>(
    () =>
      result.featureToggles.reduce<Record<string, boolean>>((acc, feature) => {
        acc[feature.id] = feature.active;
        return acc;
      }, {})
  );
  const [qrOpen, setQrOpen] = useState(false);

  const activeFeatureIds = useMemo(
    () =>
      Object.entries(featureStates)
        .filter(([, active]) => active)
        .map(([id]) => id),
    [featureStates]
  );
  useEffect(() => {
    onFeaturesChange?.(activeFeatureIds);
  }, [activeFeatureIds, onFeaturesChange]);

  const currentMagicLink = useMemo(
    () => buildMagicLink(result.baseUrl, result.sessionKey, activeFeatureIds),
    [activeFeatureIds, result.baseUrl, result.sessionKey]
  );

  const currentEmbed = useMemo(
    () => buildEmbedCode(result.sessionKey, activeFeatureIds),
    [activeFeatureIds, result.sessionKey]
  );

  const togglesWithState = useMemo(
    () =>
      result.featureToggles.map((feature) => ({
        ...feature,
        active: featureStates[feature.id] ?? false,
      })),
    [result.featureToggles, featureStates]
  );

const handleToggleChange = (featureId: string, active: boolean) => {
  setFeatureStates(prev => {
    const next = { ...prev, [featureId]: active };

    // highlights <-> stream_understanding lockstep (kept from your code)
    if (featureId === "highlights") {
      next.stream_understanding = active;
    }
    if (featureId === "stream_understanding") {
      next.highlights = active;
    }

    // ✅ Chat dependency: turning on either of these turns on chat_room
    if ((featureId === "ai_chat_engagement" || featureId === "ai_moderation") && active) {
      next.chat_room = true;
    }

    return next;
  });
};



  return (
    <>
      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: PREVIEW_TOTAL_H,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",   // <-- this centers the chip AND the preview
            }}
          >
            {/* Preview label */}
            <Chip
              label="Preview"
              size="small"
              sx={{
                mb: 1,
                fontWeight: 700,
                color: "#C7D2FE",
                bgcolor: alpha("#6366F1", 0.18),
                border: `1px solid ${alpha("#6366F1", 0.35)}`,
                borderRadius: 2,
                letterSpacing: 0.4,
              }}
            />

            {/* The preview panel itself */}
            <PreviewPanel features={features} activeIds={activeFeatureIds} />
          </Box>
        </Grid>

<Grid item xs={12} md={6}>
  {/* Right column: extends to match the content flow */}
  <Box 
    sx={{ 
      minHeight: PREVIEW_TOTAL_H,
      height: "auto", // Let it grow naturally
      display: "flex", 
      flexDirection: "column" 
    }}
  >
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        borderRadius: 4,
        border: `1px solid ${alpha("#FFFFFF", 0.1)}`,
        bgcolor: "rgba(15, 20, 32, 0.6)",
        backdropFilter: "blur(24px)",
        px: 3,
        py: 3,
        maxHeight: PREVIEW_TOTAL_H * 1.3, // Cap it at 30% taller
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: alpha("#FFFFFF", 0.02),
          borderRadius: 3,
        },
        "&::-webkit-scrollbar-thumb": {
          background: alpha("#6366F1", 0.3),
          borderRadius: 3,
          "&:hover": {
            background: alpha("#6366F1", 0.5),
          },
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#FFFFFF",
          fontWeight: 700,
          fontSize: 18,
          mb: 3,
          letterSpacing: 0.2,
        }}
      >
        Customize Features
      </Typography>

      <FeatureTuner
        featureToggles={togglesWithState}
        onChange={handleToggleChange}
      />
      
      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          borderTop: `1px solid ${alpha("#FFFFFF", 0.06)}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: alpha("#FFFFFF", 0.4),
            fontSize: 12,
            lineHeight: 1.6,
            display: "block",
          }}
        >
          💡 Changes apply instantly to your magic link and embed code
        </Typography>
      </Box>
    </Box>
  </Box>
</Grid>
      </Grid>


      <Grid container spacing={4} sx={{ mt: 4 }}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              bgcolor: alpha("#000000", 0.2),
              borderRadius: 5,
              border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
              px: { xs: 3, md: 4 },
              py: { xs: 3, md: 4 },
              backdropFilter: "blur(20px)",
            }}
          >
            <Typography variant="h5" sx={{ color: "#FFFFFF", fontWeight: 700, mb: 1 }}>
              Ready to Deploy
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: alpha("#FFFFFF", 0.6), mb: 3, lineHeight: 1.7 }}
            >
              Scan or share the magic link to open this configuration instantly
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setQrOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 16,
                py: 1.5,
                px: 4,
                boxShadow: `0 8px 32px ${alpha("#6366F1", 0.4)}`,
                "&:hover": {
                  background: "linear-gradient(135deg, #5558E3 0%, #7C3AED 100%)",
                  boxShadow: `0 12px 40px ${alpha("#6366F1", 0.5)}`,
                },
              }}
            >
              View QR Code
            </Button>
            <Button
              component="a"
              href={`/immersive-panel-admin?session=${result.sessionKey}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: 3,
                fontWeight: 600,
                fontSize: 16,
                px: 4,
                py: 1.5,
                ml: { xs: 0, sm: 2 },
                mt: { xs: 2, sm: 0 },
                color: alpha("#6366F1", 0.9),
                borderColor: alpha("#6366F1", 0.4),
                "&:hover": {
                  borderColor: alpha("#6366F1", 0.8),
                  bgcolor: alpha("#6366F1", 0.12),
                },
              }}
            >
              Open Admin Control Panel
            </Button>
            <Box
              sx={{
                mt: 3,
                bgcolor: alpha("#FFFFFF", 0.03),
                p: 3,
                borderRadius: 4,
                border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: alpha("#FFFFFF", 0.5),
                  fontWeight: 600,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                }}
              >
                Magic Link
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#FFFFFF",
                  mt: 1.5,
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: 13,
                  lineHeight: 1.8,
                }}
              >
                {currentMagicLink}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              bgcolor: alpha("#000000", 0.2),
              borderRadius: 5,
              border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
              px: { xs: 3, md: 4 },
              py: { xs: 3, md: 4 },
              height: "100%",
              backdropFilter: "blur(20px)",
            }}
          >
            <Typography variant="h5" sx={{ color: "#FFFFFF", fontWeight: 700, mb: 1 }}>
              Embed in React
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: alpha("#FFFFFF", 0.6), mb: 3, lineHeight: 1.7 }}
            >
              Drop this component into your frontend
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: alpha("#000000", 0.5),
                borderRadius: 4,
                border: `1px solid ${alpha("#FFFFFF", 0.06)}`,
                p: 3,
                overflowX: "auto",
                color: "#E0E7FF",
                fontSize: 13,
                lineHeight: 1.7,
                fontFamily:
                  '"Fira Code", "SF Mono", Monaco, Consolas, "Liberation Mono", monospace',
              }}
            >
              {currentEmbed}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 6,
        }}
      >
        <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.4) }}>
          Session: {result.sessionKey}
        </Typography>
        <Button
          variant="text"
          onClick={onRestart}
          startIcon={<RestartAltIcon />}
          sx={{
            textTransform: "none",
            color: alpha("#FFFFFF", 0.6),
            "&:hover": {
              color: "#FFFFFF",
              bgcolor: alpha("#FFFFFF", 0.05),
            },
          }}
        >
          Start Over
        </Button>
      </Box>

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: "#FFFFFF",
          },
        }}
      >
        <DialogContent
          sx={{
            px: 4,
            py: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A0F1C" }}>
            Scan to Launch Panel
          </Typography>
          <QRCode
            value={currentMagicLink}
            size={260}
            bgColor="#FFFFFF"
            fgColor="#0A0F1C"
            qrStyle="dots"
          />
          <Typography
            variant="body2"
            sx={{
              color: alpha("#0A0F1C", 0.6),
              textAlign: "center",
              lineHeight: 1.7,
            }}
          >
            Share this QR code to instantly launch your configured immersive panel
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ImmersivePanelSetupPage: React.FC = () => {
  const [catalog, setCatalog] = useState<CatalogResponse>(FALLBACK_CATALOG);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [stageHistory, setStageHistory] = useState<Stage[]>(["experience"]);
  const [experienceType, setExperienceType] = useState<ExperienceType | null>(null);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDetails, setEventDetails] = useState("");
  const [streamUrl, setStreamUrl] = useState("");
  const [hasStreamUrl, setHasStreamUrl] = useState(true);
  const [streamDescription, setStreamDescription] = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecommendationResult | null>(null);
  const [latestFeatures, setLatestFeatures] = useState<string[]>([]);
  const persistSignatureRef = useRef<string>("");
  const handleFeaturesChange = useCallback((features: string[]) => {
    setLatestFeatures(features);
  }, []);

  const currentStage = stageHistory[stageHistory.length - 1];

  useEffect(() => {
    let mounted = true;
    axios
      .get<CatalogResponse>(`${AI_API_URL}/immersive-setup/catalog`)
      .then((response) => {
        if (mounted) {
          setCatalog(response.data);
          setCatalogError(null);
        }
      })
      .catch(() => {
        if (mounted) {
          setCatalog(FALLBACK_CATALOG);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (currentStage !== "result" || !result || !experienceType) {
      return;
    }

    const activeFeatureIds =
      latestFeatures.length > 0
        ? latestFeatures
        : result.featureToggles.filter((feature) => feature.active).map((feature) => feature.id);

    if (!activeFeatureIds.length) {
      return;
    }

    const answers =
      experienceType === "event"
        ? {
            title: eventTitle,
            description: eventDetails,
          }
        : {
            media_source: streamUrl,
            has_source: hasStreamUrl,
            description: streamDescription,
          };

    const chatSeed = DEFAULT_CHAT_STARTER.map((message) => ({
      ...message,
    }));

    const payload = {
      session_id: result.sessionKey,
      experience_type: experienceType,
      features: activeFeatureIds,
      answers,
      chat: chatSeed,
    };
    const signature = JSON.stringify(payload);
    if (persistSignatureRef.current === signature) {
      return;
    }

    axios
      .post(`${AI_API_URL}/immersive-runtime/session`, payload)
      .then(() => {
        persistSignatureRef.current = signature;
      })
      .catch((err) => {
        console.error("Failed to persist immersive session", err);
      });
  }, [
    currentStage,
    result,
    experienceType,
    eventTitle,
    eventDetails,
    streamUrl,
    hasStreamUrl,
    streamDescription,
    latestFeatures,
  ]);

  const featuresForCurrentExperience = useMemo(() => {
    if (!experienceType) {
      return [];
    }
    return catalog[experienceType];
  }, [catalog, experienceType]);

  const goToStage = (nextStage: Stage) => {
    setStageHistory((history) => [...history, nextStage]);
  };

  const goBack = () => {
    setStageHistory((history) =>
      history.length > 1 ? history.slice(0, -1) : history
    );
    setError(null);
  };

  const handleExperienceSelection = (type: ExperienceType) => {
    setExperienceType(type);
    setSelectedFeatures([]);
    if (type === "event") {
      goToStage("event_details");
    } else {
      goToStage("stream_source");
      setHasStreamUrl(true);
    }
  };

  const handleNext = () => {
    setError(null);
    if (currentStage === "event_details") {
      goToStage("event_features");
    } else if (currentStage === "stream_source") {
      goToStage("stream_about");
    } else if (currentStage === "stream_about") {
      goToStage("stream_features");
    } else if (
      currentStage === "event_features" ||
      currentStage === "stream_features"
    ) {
      submitForRecommendation();
    }
  };

const submitForRecommendation = async () => {
  if (!experienceType || selectedFeatures.length === 0) {
    setError("Please select at least one feature to continue");
    return;
  }

  const payload: RecommendationPayload = {
    experience_type: experienceType,
    selected_feature_ids: selectedFeatures,
  };

  if (experienceType === "event") {
    payload.event = { title: eventTitle, description: eventDetails };
  } else {
    payload.stream = {
      media_source: streamUrl || null,
      has_source: hasStreamUrl,
      description: streamDescription,
    };
  }

  setLoading(true);
  const startedAt = performance.now();

  try {
    const response = await axios.post<RecommendationResponse>(
      `${AI_API_URL}/immersive-setup/recommendation`,
      payload
    );

    const data = response.data;

    const proceed = () => {
      setResult({
        sessionKey: data.session_key,
        baseUrl: API_ORIGIN,
        initialMagicLink: data.magic_link,
        featureToggles: data.feature_toggles,
        embedCodeTsx: data.embed_code_tsx,
        experienceSummary: data.experience_summary,
      });
      goToStage("result");
      setLoading(false);
    };

    // Ensure a minimum 3s loader
    const elapsed = performance.now() - startedAt;
    const remaining = Math.max(0, 3000 - elapsed);
    if (remaining > 0) {
      setTimeout(proceed, remaining);
    } else {
      proceed();
    }
  } catch (requestError) {
    setError("Unable to create configuration. Please try again");
    setLoading(false);
  }
};


  const resetFlow = () => {
    setStageHistory(["experience"]);
    setExperienceType(null);
    setEventTitle("");
    setEventDetails("");
    setStreamUrl("");
    setHasStreamUrl(true);
    setStreamDescription("");
    setSelectedFeatures([]);
    setResult(null);
    setError(null);
    setLatestFeatures([]);
    persistSignatureRef.current = "";
  };

  const handleFeatureToggle = (featureId: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  };

  const canContinue = useMemo(() => {
    if (currentStage === "experience") {
      return Boolean(experienceType);
    }
    if (currentStage === "event_details") {
      return eventTitle.trim().length > 0;
    }
    if (
      currentStage === "event_features" ||
      currentStage === "stream_features"
    ) {
      return selectedFeatures.length > 0;
    }
    if (currentStage === "stream_source") {
      return hasStreamUrl ? streamUrl.trim().length > 0 : true;
    }
    return true;
  }, [
    currentStage,
    eventTitle,
    experienceType,
    selectedFeatures,
    hasStreamUrl,
    streamUrl,
  ]);

  const renderForm = () => {
    if (currentStage === "experience") {
      return (
        <Grid container spacing={4} sx={{ maxWidth: 1250, mx: "auto" }}>
          <Grid item xs={12} md={6}>
            <OptionCard
              title="Online Stream"
              description="Launch a companion experience for your live stream, simulcast, or on-demand content"
              onClick={() => handleExperienceSelection("stream")}
              selected={experienceType === "stream"}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard
              title="In-Person Event"
              description="Bring your event into one hub with logistics assistance, chat, and on-site engagement"
              onClick={() => handleExperienceSelection("event")}
              selected={experienceType === "event"}
            />
          </Grid>
        </Grid>
      );
    }

    if (currentStage === "event_details") {
      return (
        <Box sx={{ maxWidth: 875, mx: "auto", width: "100%" }}>
          <Stack spacing={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Event Name"
              placeholder="e.g. BoltCon 2025 Keynote"
              value={eventTitle}
              onChange={(event) => setEventTitle(event.target.value)}
              InputProps={{
                sx: {
                  bgcolor: alpha("#FFFFFF", 0.03),
                  borderRadius: 3,
                  color: "#FFFFFF",
                  fontSize: 18,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha("#FFFFFF", 0.1),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha("#FFFFFF", 0.2),
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6366F1",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: alpha("#FFFFFF", 0.6),
                  "&.Mui-focused": {
                    color: "#6366F1",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={5}
              variant="outlined"
              label="Event Details (copy paste)"
              placeholder="Copy paste your event page or provide a description and logistics..."
              value={eventDetails}
              onChange={(event) => setEventDetails(event.target.value)}
              InputProps={{
                sx: {
                  bgcolor: alpha("#FFFFFF", 0.03),
                  borderRadius: 3,
                  color: "#FFFFFF",
                  fontSize: 16,
                  alignItems: "flex-start",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha("#FFFFFF", 0.1),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha("#FFFFFF", 0.2),
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6366F1",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: alpha("#FFFFFF", 0.6),
                  "&.Mui-focused": {
                    color: "#6366F1",
                  },
                },
              }}
            />
          </Stack>
        </Box>
      );
    }

    if (currentStage === "stream_source") {
      return (
        <Box sx={{ maxWidth: 875, mx: "auto", width: "100%" }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Stream URL (hls or m3u8 url)"
              placeholder="https://example.com/stream.m3u8"
              value={streamUrl}
              onChange={(event) => setStreamUrl(event.target.value)}
              disabled={!hasStreamUrl}
              InputProps={{
                sx: {
                  bgcolor: hasStreamUrl
                    ? alpha("#FFFFFF", 0.03)
                    : alpha("#FFFFFF", 0.01),
                  borderRadius: 3,
                  color: hasStreamUrl ? "#FFFFFF" : alpha("#FFFFFF", 0.3),
                  fontSize: 16,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha("#FFFFFF", 0.1),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha("#FFFFFF", 0.2),
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6366F1",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: alpha("#FFFFFF", 0.6),
                  "&.Mui-focused": {
                    color: "#6366F1",
                  },
                },
              }}
            />
            <Button
              variant="text"
              onClick={() => setHasStreamUrl((prev) => !prev)}
              sx={{
                textTransform: "none",
                alignSelf: "flex-start",
                color: alpha("#FFFFFF", 0.6),
                fontSize: 15,
                "&:hover": {
                  color: "#FFFFFF",
                  bgcolor: alpha("#FFFFFF", 0.05),
                },
              }}
            >
              {hasStreamUrl
                ? "I don't have the URL yet"
                : "I have the stream URL"}
            </Button>
          </Stack>
        </Box>
      );
    }

    if (currentStage === "stream_about") {
      return (
        <Box sx={{ maxWidth: 875, mx: "auto", width: "100%" }}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            variant="outlined"
            label="Stream Description"
            placeholder="Tell us about your stream, the content, audience, or any special requirements..."
            value={streamDescription}
            onChange={(event) => setStreamDescription(event.target.value)}
            InputProps={{
              sx: {
                bgcolor: alpha("#FFFFFF", 0.03),
                borderRadius: 3,
                color: "#FFFFFF",
                fontSize: 16,
                alignItems: "flex-start",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#FFFFFF", 0.1),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#FFFFFF", 0.2),
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6366F1",
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: alpha("#FFFFFF", 0.6),
                "&.Mui-focused": {
                  color: "#6366F1",
                },
              },
            }}
          />
        </Box>
      );
    }

    if (
      currentStage === "event_features" ||
      currentStage === "stream_features"
    ) {
      return (
        <FeatureSelector
          features={featuresForCurrentExperience}
          selectedIds={selectedFeatures}
          onToggle={handleFeatureToggle}
        />
      );
    }

    return null;
  };

  return (
    <PageShell>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        {!loading && (
      <SectionHeader
        stage={currentStage}
        experienceType={experienceType}
        logo={
          currentStage === "experience" ? (
          <Box
            sx={{ width: { xs: 120, sm: 150, lg: 175 }, height: 35, lineHeight: 0 }}
            aria-label="Company logo"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 175 35"
              width="100%"
              height="100%"
              role="img"
              aria-label="Company logo"
            >
              <g clipPath="url(#clip0_7400_133)">
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M40.936 3.978c-8.468.074-13.998 5.035-13.923 13.645.075 8.61 5.691 13.473 14.158 13.4 8.468-.075 13.998-5.035 13.923-13.645-.076-8.61-5.692-13.474-14.158-13.4zM.224 30.665l13.254-.116c4.894-.043 10.773-1.88 10.723-7.668-.028-3.161-1.955-4.985-4.91-5.816 2.494-.755 3.96-2.625 3.938-5.251-.05-5.663-5.58-6.918-10.243-6.878L0 5.05l.224 25.615zm7.5-6.336l-.032-3.787 5.698-.05c1.375-.012 3.091.099 3.107 1.867.015 1.768-1.698 1.909-3.074 1.92l-5.698.05zm5.348-9.568l-5.43.047-.031-3.537 5.43-.047c1.233-.01 2.557.264 2.57 1.746.013 1.483-1.306 1.78-2.539 1.79zM66.11 4.473L66.28 23.8l12.272-.107.055 6.288-19.828.173-.224-25.615 7.556-.066zm8.576 6.213l8.377-.073.169 19.328 7.556-.066-.169-19.328 8.378-.073-.055-6.288-24.311.212.055 6.288zM36.949 24.252l10.92-6.432-11.03-6.24.11 12.672z"
                />
                <path fill="url(#paint0_linear_7400_133)" d="M136.648 23.677v2.443c0 1.813.338 3.273 1.015 4.379a7.078 7.078 0 002.719 2.581l4.333-2.489c-.922-.215-1.675-.522-2.259-.922a3.46 3.46 0 01-1.198-1.475c-.246-.614-.369-1.336-.369-2.166v-2.351h-4.241z" />
                <path fill="#fff" d="M136.648 23.677v2.443c0 1.813.338 3.273 1.015 4.379a7.078 7.078 0 002.719 2.581l4.333-2.489c-.922-.215-1.675-.522-2.259-.922a3.46 3.46 0 01-1.198-1.475c-.246-.614-.369-1.336-.369-2.166v-2.351h-4.241z" />
                <path fill="url(#paint1_linear_7400_133)" d="M144.346.86c-1.721.245-3.166.722-4.333 1.428l7.098 2.029c1.506-.123 3.35-.185 5.532-.185h4.794c2.95 0 5.255.077 6.914.23 1.69.154 2.935.462 3.734.923.83.46 1.337 1.106 1.521 1.936.184.799.292 1.859.323 3.18h4.24V9.25c0-1.475-.215-2.766-.645-3.872-.399-1.106-1.122-2.028-2.166-2.766-1.045-.737-2.474-1.275-4.287-1.613-1.813-.369-4.118-.553-6.915-.553h-9.726c-2.335 0-4.363.138-6.084.414z" />
                <path fill="#fff" d="M144.346.86c-1.721.245-3.166.722-4.333 1.428l7.098 2.029c1.506-.123 3.35-.185 5.532-.185h4.794c2.95 0 5.255.077 6.914.23 1.69.154 2.935.462 3.734.923.83.46 1.337 1.106 1.521 1.936.184.799.292 1.859.323 3.18h4.24V9.25c0-1.475-.215-2.766-.645-3.872-.399-1.106-1.122-2.028-2.166-2.766-1.045-.737-2.474-1.275-4.287-1.613-1.813-.369-4.118-.553-6.915-.553h-9.726c-2.335 0-4.363.138-6.084.414z" />
                <path fill="#fff" d="M144.689 23.77V11.23c0-1.443-.215-2.627-.645-3.548-.4-.922-.953-1.645-1.66-2.167-.707-.522-1.536-.876-2.489-1.06a13.79 13.79 0 00-3.042-.323h-18.3c-1.076 0-2.09.108-3.042.323-.953.184-1.783.538-2.49 1.06-.706.522-1.275 1.245-1.705 2.167-.4.921-.599 2.105-.599 3.549v12.538c0 1.444.199 2.628.599 3.55.43.921.999 1.643 1.705 2.166.707.522 1.537.891 2.49 1.106.952.185 1.966.277 3.042.277h18.3a16 16 0 003.042-.277c.953-.215 1.782-.584 2.489-1.106.707-.523 1.26-1.245 1.66-2.167.43-.922.645-2.105.645-3.549zm-38.49-1.707v-9.126c0-2.12.185-3.95.553-5.486.4-1.567 1.061-2.873 1.982-3.918.922-1.045 2.136-1.813 3.642-2.305 1.537-.522 3.442-.783 5.716-.783h19.222c2.243 0 4.118.26 5.623.783 1.537.492 2.766 1.26 3.688 2.305.922 1.045 1.583 2.35 1.982 3.918.4 1.537.599 3.365.599 5.486v9.126c0 2.121-.199 3.965-.599 5.532-.399 1.537-1.06 2.827-1.982 3.872-.922 1.045-2.151 1.828-3.688 2.35-1.505.493-3.38.738-5.623.738h-19.222c-2.274 0-4.179-.245-5.716-.737-1.506-.523-2.72-1.306-3.642-2.351-.921-1.045-1.582-2.335-1.982-3.872-.368-1.567-.553-3.411-.553-5.532z" />
                <path fill="url(#paint2_linear_7400_133)" d="M162.278 30.868h-14.013c-.398 0-.778-.008-1.14-.022-.18.28-1.467 1.412-1.62 1.54-1.081.9-1.981 1.202-2.881 1.533.532.136 1.091.24 1.676.314 1.506.215 3.089.323 4.748.323h12.538c1.66 0 3.288-.077 4.886-.231 1.629-.154 3.073-.522 4.333-1.106a7.206 7.206 0 003.043-2.628c.768-1.168 1.152-2.78 1.152-4.84v-2.166c0-2.828-.983-4.902-2.95-6.223-1.967-1.322-5.147-1.982-9.542-1.982h-13.367c-1.752 0-3.181-.093-4.287-.277-1.076-.184-1.921-.476-2.536-.876a3.24 3.24 0 01-1.198-1.66c-.184-.675-.277-1.505-.277-2.488 0-1.383.2-2.459.6-3.227.263-.49.619-.904 1.067-1.243-.967-.772-1.867-.952-2.407-1.132-.54-.18-1.7-.303-2.129-.303a6.962 6.962 0 00-.542.926c-.553 1.199-.829 2.75-.829 4.656 0 2.213.307 3.964.921 5.255.615 1.29 1.629 2.258 3.043 2.904 1.444.645 3.334 1.06 5.669 1.244 2.336.185 5.225.277 8.666.277h8.574c1.414 0 2.582.077 3.503.23.953.154 1.706.43 2.259.83.553.4.937.937 1.153 1.614.245.676.368 1.536.368 2.581 0 1.199-.138 2.197-.415 2.996-.276.8-.737 1.43-1.382 1.89-.646.461-1.522.8-2.628 1.014-1.075.185-2.428.277-4.056.277z" />
                <path fill="#fff" d="M162.278 30.868h-14.013c-.398 0-.778-.008-1.14-.022-.18.28-1.467 1.412-1.62 1.54-1.081.9-1.981 1.202-2.881 1.533.532.136 1.091.24 1.676.314 1.506.215 3.089.323 4.748.323h12.538c1.66 0 3.288-.077 4.886-.231 1.629-.154 3.073-.522 4.333-1.106a7.206 7.206 0 003.043-2.628c.768-1.168 1.152-2.78 1.152-4.84v-2.166c0-2.828-.983-4.902-2.95-6.223-1.967-1.322-5.147-1.982-9.542-1.982h-13.367c-1.752 0-3.181-.093-4.287-.277-1.076-.184-1.921-.476-2.536-.876a3.24 3.24 0 01-1.198-1.66c-.184-.675-.277-1.505-.277-2.488 0-1.383.2-2.459.6-3.227.263-.49.619-.904 1.067-1.243-.967-.772-1.867-.952-2.407-1.132-.54-.18-1.7-.303-2.129-.303a6.962 6.962 0 00-.542.926c-.553 1.199-.829 2.75-.829 4.656 0 2.213.307 3.964.921 5.255.615 1.29 1.629 2.258 3.043 2.904 1.444.645 3.334 1.06 5.669 1.244 2.336.185 5.225.277 8.666.277h8.574c1.414 0 2.582.077 3.503.23.953.154 1.706.43 2.259.83.553.4.937.937 1.153 1.614.245.676.368 1.536.368 2.581 0 1.199-.138 2.197-.415 2.996-.276.8-.737 1.43-1.382 1.89-.646.461-1.522.8-2.628 1.014-1.075.185-2.428.277-4.056.277z" />
              </g>
              <defs>
                <linearGradient id="paint0_linear_7400_133" x1="136.648" x2="144.715" y1="28.379" y2="28.379" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#00C6FF" />
                  <stop offset="1" stopColor="#0072FF" />
                </linearGradient>
                <linearGradient id="paint1_linear_7400_133" x1="140.013" x2="174.169" y1="5.423" y2="5.423" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#00C6FF" />
                  <stop offset="1" stopColor="#0072FF" />
                </linearGradient>
                <linearGradient id="paint2_linear_7400_133" x1="136.603" x2="175" y1="19.365" y2="19.365" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#00C6FF" />
                  <stop offset="1" stopColor="#0072FF" />
                </linearGradient>
                <clipPath id="clip0_7400_133">
                  <path fill="#fff" d="M0 0H175V34.111H0z" transform="translate(0 .445)" />
                </clipPath>
              </defs>
            </svg>
          </Box>
          ) : undefined
        }
      />
      )}

        {catalogError && (
          <Box
            sx={{
              mb: 4,
              bgcolor: alpha("#EF4444", 0.1),
              border: `1px solid ${alpha("#EF4444", 0.3)}`,
              borderRadius: 4,
              px: 4,
              py: 3,
              textAlign: "center",
              maxWidth: 875,
              mx: "auto",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#FCA5A5", fontSize: 15, lineHeight: 1.6 }}
            >
              {catalogError}
            </Typography>
          </Box>
        )}

          {loading ? (
            <FancyLoader
              title="Creating your panel…"
              subtitle="We’re assembling your configuration and generating the QR code"
            />
          ) : currentStage === "result" && result ? (
          <RecommendationView
            features={featuresForCurrentExperience}
            result={result}
            onRestart={resetFlow}
            onFeaturesChange={handleFeaturesChange}
          />
        ) : (
          <>
            {renderForm()}

            {error && (
              <Typography
                variant="body2"
                sx={{
                  color: "#FCA5A5",
                  mt: 4,
                  textAlign: "center",
                  fontSize: 15,
                }}
              >
                {error}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 8,
                maxWidth: 875,
                mx: "auto",
              }}
            >
              {stageHistory.length > 1 && currentStage !== "experience" ? (
                <Button
                  variant="text"
                  onClick={goBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    textTransform: "none",
                    color: alpha("#FFFFFF", 0.6),
                    fontSize: 16,
                    "&:hover": {
                      color: "#FFFFFF",
                      bgcolor: alpha("#FFFFFF", 0.05),
                    },
                  }}
                >
                  Back
                </Button>
              ) : (
                <Box />
              )}

              {currentStage !== "experience" && currentStage !== "result" && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canContinue}
                  sx={{
                    background: canContinue
                      ? "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)"
                      : alpha("#FFFFFF", 0.1),
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 16,
                    px: 4,
                    py: 1.5,
                    boxShadow: canContinue
                      ? `0 8px 32px ${alpha("#6366F1", 0.4)}`
                      : "none",
                    color: canContinue ? "#FFFFFF" : alpha("#FFFFFF", 0.3),
                    "&:hover": {
                      background: canContinue
                        ? "linear-gradient(135deg, #5558E3 0%, #7C3AED 100%)"
                        : alpha("#FFFFFF", 0.1),
                      boxShadow: canContinue
                        ? `0 12px 40px ${alpha("#6366F1", 0.5)}`
                        : "none",
                    },
                    "&:disabled": {
                      background: alpha("#FFFFFF", 0.05),
                      color: alpha("#FFFFFF", 0.3),
                    },
                  }}
                >
                  {currentStage === "event_features" ||
                  currentStage === "stream_features"
                    ? "Create Panel"
                    : "Continue"}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </PageShell>
  );
};

export default ImmersivePanelSetupPage;
